import { z } from 'zod';

const NAVIGATION_PERMISSIONS = [
  'requests.read',
  'requests.manage',
  // WHY (#2799, #2801): `logist_cn` holds the batch-export gate after its
  // Phase-2 grant completed once every backend recognised the wire literal.
  'requests.export.bulk',
  'deals.read',
  'deals.manage',
  // INVARIANT (#698): `shipments.*` renamed to `cargo.*` to align with the
  //   "Грузы" menu label. The catalog row was renamed in-place via the
  //   data migration `rename_shipments_to_cargo_and_seed_logistics_permissions`;
  //   no `shipments.*` code is recognised after that point.
  'cargo.read',
  'cargo.manage',
  // INVARIANT (#1227): granular Cargo ("Грузы") action codes for the
  //   NextKargo port. `cargo.read`/`cargo.manage` above are kept for legacy
  //   compatibility, but the new behaviour is gated by these granular codes
  //   — `cargo.manage` is NOT granted to any role for Cargo any more and
  //   does NOT imply any of the actions below. Row-scope enforcement
  //   (own-row vs all-rows) ships in #1228/#1230; `cargo.scope.all` lives in
  //   `SCOPE_PERMISSIONS`. The china-payment vs carrier-payment split mirrors
  //   the source field-level role split (logist_cn edits china payment;
  //   logist_ru does not).
  'cargo.import',
  'cargo.create',
  'cargo.update',
  'cargo.delete',
  'cargo.clientTariff.manage',
  'cargo.clientPayment.manage',
  // RESERVED/DEPRECATED (#1935, epic #1926 Decision 8): the quick client-payment
  //   path is removed and replaced by the review workflow; its grants are revoked
  //   from manager + rop. The code is KEPT for append-only / FK compatibility —
  //   historical `role_permissions` rows referencing it stay valid. Do NOT delete.
  'cargo.clientPayment.quickManage',
  'cargo.clientPaymentDocs.manage',
  'cargo.clientPayment.review',
  'cargo.chinaPayment.manage',
  'cargo.carrierPayment.manage',
  'cargo.dictionaries.read',
  'cargo.dictionaries.manage',
  'calls.read',
  'calls.manage',
  'chat.read',
  'chat.manage',
  'tasks.read',
  'tasks.manage',
  'clients.read',
  'clients.manage',
  // INVARIANT (#601): `companies.*` is deprecated/reserved for append-only
  //   FK compatibility and is not granted to seeded non-admin roles since
  //   #601 — the MVP Clients feature uses `clients.*` and gates nested
  //   legal entities under client access.
  'companies.read',
  'companies.manage',
  'leads.read',
  'leads.manage',
  // WHY (#2345, #2639): `rop` holds the grant for assigning an active,
  //   unassigned Lead to an eligible manager.
  'leads.assign',
  'paymentClaims.read',
  'paymentClaims.manage',
  'payments.read',
  'payments.manage',
  'accounts.read',
  'accounts.manage',
  'stages.read',
  'stages.manage',
  'sources.read',
  'sources.manage',
  'warehouses.read',
  'warehouses.manage',
  'priceLists.read',
  'priceLists.manage',
  'carriers.read',
  'carriers.manage',
  'users.read',
  'users.manage',
  'departments.read',
  'departments.manage',
  'roles.read',
  'roles.manage',
  'audit.read',
  // INVARIANT (#623): reference-data directories. `transportTypes.*` and
  //   `arrivalCities.*` are read by `logist_cn` and `logist_ru` and
  //   managed by both shift roles plus admin (via the `ALL` sentinel).
  //   Manager gets read-only grants for request creation pickers; write
  //   stays with logist/admin roles.
  'transportTypes.read',
  'transportTypes.manage',
  'arrivalCities.read',
  'arrivalCities.manage',
  // INVARIANT (#995): request-level dictionaries. `roads.*` and
  //   `packaging.*` are read+managed by `logist_cn` and admin (via the
  //   `ALL` sentinel); manager is read-only for both. INVARIANT (#1947):
  //   `logist_ru` also holds `roads.read` (read-only, for the cargo edit
  //   modal road picker); `roads.manage` stays admin + `logist_cn` only.
  'roads.read',
  'roads.manage',
  'packaging.read',
  'packaging.manage',
  // INVARIANT (#1166): RU-internal carriers directory. Separate from the
  //   global `carriers.*` (China→RU directory): RU-side ad-hoc carriers
  //   with contact PII the manager must not see. `russiaCarriers.*` is
  //   held by `logist_ru` only (plus `admin` via the `ALL` sentinel);
  //   `manager` and `logist_cn` MUST NOT receive it.
  'russiaCarriers.read',
  'russiaCarriers.manage',
  // INVARIANT (#698): Russia-side request namespace + China warehouses
  //   directory. The Russia request namespace mirrors `requests.*` in
  //   structure (read/manage/scope.all + transitions) so the controller
  //   guard pattern matches. `russiaRequests.*` is held by `logist_ru`
  //   and `manager`; `logist_cn` MUST NOT receive it.
  'russiaRequests.read',
  'russiaRequests.manage',
  'warehousesChina.read',
  'warehousesChina.manage',
  // INVARIANT (#1194): commercial proposals ("КП") — a new standalone
  //   aggregate. `commercialProposals.read` gates visibility of the
  //   proposals workspace; `commercialProposals.manage` gates create /
  //   edit / publish / revoke. `manager` holds both codes after the Phase-2
  //   grant completed in #1195.
  //   Admin authorises via the `ALL` sentinel and needs no grant row.
  'commercialProposals.read',
  'commercialProposals.manage',
  // INVARIANT (#2760, #2769): `manager` holds both logistics-calculation
  //   codes after their Phase-2 grant completed across all backend instances.
  'logisticsCalculations.read',
  'logisticsCalculations.manage',
  // WHY (#2783, #2862, #2869): cargo-problems codes roll out dark. The original
  // set completed #2787; these workspace actions completed their Phase-2 grants
  // to `rop` and `executive` in #2869.
  'cargoProblems.read',
  'cargoProblems.create',
  'cargoProblems.work.china',
  'cargoProblems.work.russia',
  'cargoProblems.assignee.manage',
  'cargoProblems.attachments.delete',
  // INVARIANT (#1587): leadership Dashboards section. `dashboard.read` is the
  //   single code gating the whole Dashboards group (first surface:
  //   `/dashboards/china-requests`). The Phase-2 grant completed in #1591:
  //   `executive` holds the code; admin resolves it at runtime via `ALL`
  //   and needs no materialized grant row.
  'dashboard.read',
  // INVARIANT (#2007): `agent.admin` gates v2 agent administration
  //   (prompt/glossary config) and debug SQL/trace visibility — NOT the
  //   agent's data access (that stays `dashboard.read`). Catalog-grow only:
  //   NO role holds it; admin authorises via the `ALL` sentinel; the seed
  //   withholds the admin row via `ADMIN_MATERIALIZATION_EXCLUDED_CODES`.
  'agent.admin',
] as const;

const TRANSITION_PERMISSIONS = [
  'requests.transition.send',
  'requests.transition.return',
  'requests.transition.process',
  'requests.transition.close',
  'requests.transition.archive',
  'requests.transition.unarchive',
  // WHY (#585): manager-side customer-decision finalisation. Each code
  //   is enforced independently by the controller `@RequiresPermission`
  //   decorator — `requests.manage` alone does NOT authorise these.
  'requests.transition.success',
  'requests.transition.refuse',
  // WHY (#1059): logist_cn-side reopen of a PROCESSED China request back
  //   to IN_WORK for additional logistics work. Manager does NOT receive
  //   this code; reopen authority is the assigned logist's alone.
  'requests.transition.reopen',
  // WHY (#698, #1544): Russia-side request transitions. #1544 brings the
  //   Russia lifecycle to China parity — `success` / `refuse` (manager-side
  //   customer-decision finalisation) and `reopen` (logist_ru-side
  //   PROCESSED -> IN_WORK) now exist. Each code is enforced independently
  //   by `PermissionsGuard`.
  'russiaRequests.transition.send',
  'russiaRequests.transition.return',
  'russiaRequests.transition.process',
  'russiaRequests.transition.close',
  'russiaRequests.transition.archive',
  'russiaRequests.transition.unarchive',
  'russiaRequests.transition.success',
  'russiaRequests.transition.refuse',
  'russiaRequests.transition.reopen',
  // WHY (#1835): gates for active, non-terminal POSTPONED («Отложено») status.
  //   The Phase-2 grant completed in #1848: `manager` holds both codes via the
  //   seed + paired migration `20260620120000_grant_postpone_to_manager`.
  //   #1836/#1837 wired the transitions. Each is enforced independently by
  //   the controller `@RequiresPermission` decorator; `requests.manage` /
  //   `russiaRequests.manage` alone do NOT authorise them.
  'requests.transition.postpone',
  'russiaRequests.transition.postpone',
  // WHY (#2133): gate for the China «cannot transport» (IN_WORK ->
  //   CANNOT_TRANSPORT) transition. As of #2136, `logist_cn` holds the code
  //   (seed + paired migration); admin stays excluded from explicit materialization
  //   and authorises via the `ALL` sentinel. Enforced independently by the
  //   controller `@RequiresPermission` decorator; `requests.manage` alone does
  //   NOT authorise it.
  'requests.transition.cannotTransport',
] as const;

// WHY (#1256): capability action for the external call-center lead-intake
// epic (#1255). `callCenterLeads.submit` authorises POSTing a lead-intake
// submission. Together with `callCenterLeads.readOwn` (below, granted in
// #1402) it forms the COMPLETE grant set of the `call_center_agent` system
// role — no internal CRM/logistics/request/cargo/admin read/manage/scope.
// Code shape
// `<resource>.<action>` so the i18n tree nests it cleanly under
// `permissions.callCenterLeads.submit` and the permission grid groups it
// under the `callCenterLeads` resource.
// WHY (#1396): `callCenterLeads.readOwn` lets an external agent read back
//   ONLY their own accepted lead-intake submissions (blind snapshot — no
//   server-side state). Shipped catalog-grow ONLY in Phase 1 (#1396); its
//   Phase 2 role grant to `call_center_agent` completed in #1402 once the
//   catalog had reached every instance.
const CAPABILITY_PERMISSIONS = ['callCenterLeads.submit', 'callCenterLeads.readOwn'] as const;

// WHY: `requests.read` lets a user see the requests resource at all. Without
// any further grant the UI shows them only requests they own (manager-style
// scope). `requests.scope.all` lifts that owner-only scope so the holder sees
// every request the route guard already permits — enforced today by the web
// list/drawer, and by the backend list endpoint when it lands.
// Code shape: `<resource>.scope.<modifier>` so the i18n locale tree can nest
// it cleanly under `permissions.requests.scope.all` without colliding with
// the existing leaf string `permissions.requests.read`.
// `russiaRequests.scope.all` mirrors the same shape for the Russia-side
// pool (#698).
// `cargo.scope.all` (#1227) lifts the owner-only Cargo registry scope so the
// holder sees every Cargo row the route guard already permits (admin via the
// `ALL` sentinel, plus `rop`/`logist_cn`/`logist_ru` by grant). Manager does
// NOT hold it — managers see own rows only. Enforcement lands in #1228/#1230.
// `leads.scope.all` (#2345/#2637) is the all-row visibility capability for
// Leads; `rop` and `executive` hold it.
const SCOPE_PERMISSIONS = [
  'requests.scope.all',
  'russiaRequests.scope.all',
  'cargo.scope.all',
  'leads.scope.all',
  'cargoProblems.scope.all',
] as const;

const ALL_PERMISSION_CODES = [
  ...NAVIGATION_PERMISSIONS,
  ...TRANSITION_PERMISSIONS,
  ...SCOPE_PERMISSIONS,
  ...CAPABILITY_PERMISSIONS,
] as const;

/**
 * @aiPurpose typed catalog of every RBAC permission the platform recognises.
 * @aiInvariant cardinality is exactly 110; #2862 appended two dark workspace
 *   action codes after #2799's `requests.export.bulk` and #2783's five dark
 *   `cargoProblems.*` codes. Previously 102 (75 navigation + 21 transition + 4
 *   scope + 2 capability; #1194 added `commercialProposals.read`/`.manage`;
 *   #1227 added 12 granular `cargo.*` action codes + `cargo.scope.all`; #1256
 *   added the `callCenterLeads.submit` capability; #1396 added
 *   `callCenterLeads.readOwn`; #1587 added the `dashboard.read` navigation
 *   code; #1835 added 2 `*.transition.postpone` codes; #1935 added
 *   cargo.clientPayment.review; #2007 added the `agent.admin` navigation
 *   code; #2133 added the `requests.transition.cannotTransport` code; #2345
 *   added `leads.assign` and `leads.scope.all`); adding
 *   a code
 *   requires updating both the catalog test
 *   (`packages/shared/src/rbac/index.test.ts`) and the
 *   `permissions-catalog-vs-db-parity` integration test.
 * @aiSecurity load-bearing identifier of every authorisation gate; values
 *   become foreign keys via the `permissions` table.
 */
export const PermissionCodeSchema = z.enum(ALL_PERMISSION_CODES);

/**
 * @aiPurpose string-literal union of every recognised permission code.
 * @aiInvariant equals the runtime members of `PermissionCodeSchema.options`.
 * @aiSecurity narrow this type at every authorisation boundary.
 */
export type PermissionCode = z.infer<typeof PermissionCodeSchema>;

/**
 * @aiPurpose runtime catalog used by data migrations, dev seeds, and the
 *   permission-grid UI. Human-readable labels live as i18n keys
 *   (`permissions.<code>` in `apps/web/src/i18n/locales/{ru,en,zh-Hans}.ts`),
 *   not on the catalog entry itself.
 * @aiInvariant entries cover every member of `PermissionCode`; order is
 *   navigation block, then transition block, then scope block, then the
 *   capability block (`callCenterLeads.submit`, #1256;
 *   `callCenterLeads.readOwn`, #1396).
 * @aiSecurity edits to codes break FK references in `role_permissions`
 *   rows.
 */
export const PERMISSION_CATALOG: ReadonlyArray<{ readonly code: PermissionCode }> = [
  { code: 'requests.read' },
  { code: 'requests.manage' },
  { code: 'requests.export.bulk' },
  { code: 'deals.read' },
  { code: 'deals.manage' },
  { code: 'cargo.read' },
  { code: 'cargo.manage' },
  { code: 'cargo.import' },
  { code: 'cargo.create' },
  { code: 'cargo.update' },
  { code: 'cargo.delete' },
  { code: 'cargo.clientTariff.manage' },
  { code: 'cargo.clientPayment.manage' },
  { code: 'cargo.clientPayment.quickManage' },
  { code: 'cargo.clientPaymentDocs.manage' },
  { code: 'cargo.clientPayment.review' },
  { code: 'cargo.chinaPayment.manage' },
  { code: 'cargo.carrierPayment.manage' },
  { code: 'cargo.dictionaries.read' },
  { code: 'cargo.dictionaries.manage' },
  { code: 'calls.read' },
  { code: 'calls.manage' },
  { code: 'chat.read' },
  { code: 'chat.manage' },
  { code: 'tasks.read' },
  { code: 'tasks.manage' },
  { code: 'clients.read' },
  { code: 'clients.manage' },
  { code: 'companies.read' },
  { code: 'companies.manage' },
  { code: 'leads.read' },
  { code: 'leads.manage' },
  { code: 'leads.assign' },
  { code: 'paymentClaims.read' },
  { code: 'paymentClaims.manage' },
  { code: 'payments.read' },
  { code: 'payments.manage' },
  { code: 'accounts.read' },
  { code: 'accounts.manage' },
  { code: 'stages.read' },
  { code: 'stages.manage' },
  { code: 'sources.read' },
  { code: 'sources.manage' },
  { code: 'warehouses.read' },
  { code: 'warehouses.manage' },
  { code: 'priceLists.read' },
  { code: 'priceLists.manage' },
  { code: 'carriers.read' },
  { code: 'carriers.manage' },
  { code: 'users.read' },
  { code: 'users.manage' },
  { code: 'departments.read' },
  { code: 'departments.manage' },
  { code: 'roles.read' },
  { code: 'roles.manage' },
  { code: 'audit.read' },
  { code: 'transportTypes.read' },
  { code: 'transportTypes.manage' },
  { code: 'arrivalCities.read' },
  { code: 'arrivalCities.manage' },
  { code: 'roads.read' },
  { code: 'roads.manage' },
  { code: 'packaging.read' },
  { code: 'packaging.manage' },
  { code: 'russiaCarriers.read' },
  { code: 'russiaCarriers.manage' },
  { code: 'russiaRequests.read' },
  { code: 'russiaRequests.manage' },
  { code: 'warehousesChina.read' },
  { code: 'warehousesChina.manage' },
  { code: 'commercialProposals.read' },
  { code: 'commercialProposals.manage' },
  { code: 'logisticsCalculations.read' },
  { code: 'logisticsCalculations.manage' },
  { code: 'cargoProblems.read' },
  { code: 'cargoProblems.create' },
  { code: 'cargoProblems.work.china' },
  { code: 'cargoProblems.work.russia' },
  { code: 'cargoProblems.assignee.manage' },
  { code: 'cargoProblems.attachments.delete' },
  { code: 'dashboard.read' },
  { code: 'agent.admin' },
  { code: 'requests.transition.send' },
  { code: 'requests.transition.return' },
  { code: 'requests.transition.process' },
  { code: 'requests.transition.close' },
  { code: 'requests.transition.archive' },
  { code: 'requests.transition.unarchive' },
  { code: 'requests.transition.success' },
  { code: 'requests.transition.refuse' },
  { code: 'requests.transition.reopen' },
  { code: 'russiaRequests.transition.send' },
  { code: 'russiaRequests.transition.return' },
  { code: 'russiaRequests.transition.process' },
  { code: 'russiaRequests.transition.close' },
  { code: 'russiaRequests.transition.archive' },
  { code: 'russiaRequests.transition.unarchive' },
  { code: 'russiaRequests.transition.success' },
  { code: 'russiaRequests.transition.refuse' },
  { code: 'russiaRequests.transition.reopen' },
  { code: 'requests.transition.postpone' },
  { code: 'russiaRequests.transition.postpone' },
  { code: 'requests.transition.cannotTransport' },
  { code: 'requests.scope.all' },
  { code: 'russiaRequests.scope.all' },
  { code: 'cargo.scope.all' },
  { code: 'leads.scope.all' },
  { code: 'cargoProblems.scope.all' },
  { code: 'callCenterLeads.submit' },
  { code: 'callCenterLeads.readOwn' },
];
